import {
  GetHabitatQueryVariables,
  GraphQLResult,
  Habitat,
  HabitatByHabitatIDQueryVariables,
  ListHabitatsQuery,
  ListHabitatsQueryVariables,
} from '@akin-home/amplify';
import { notNullOrUndefined } from '@akin-home/arrays';
import { HabitatModel } from '@akin-home/habitats';

import {
  formatHabitatToModel,
  getHabitatQuery,
  listHabitatsByHabitatId,
  listHabitatsQuery,
} from '@akin-home/habitats/frontend';
import { createQueryKeySet } from '@akin-home/utils/react-query';

import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';

export * from './useDeleteHabitat';
export * from './useUpdateHabitat';
export * from './useUpdateHabitatModel';

export const habitatQueryKeys = createQueryKeySet({ key: 'habitats' });

export const useGetHabitatQuery = (
  params?: GetHabitatQueryVariables,
  options?: any
) =>
  useQuery(
    habitatQueryKeys.detail(params?.id),
    async () => [(await getHabitatQuery(params)).data.getHabitat],
    {
      enabled: !!params?.id,
      ...options,
    }
  );

export const useGetHabitat = (id?: string) => {
  const { data, ...results } = useGetHabitatQuery(id ? { id } : undefined, {
    enabled: !!id,
  });

  const habitat = useMemo(() => {
    if (data?.length) {
      return formatHabitatToModel(data[0] as Habitat);
    }

    return;
  }, [data]);

  return {
    ...results,
    habitat,
  };
};

/** Paginated habitat query */
export const useListHabitats = (
  params?: ListHabitatsQueryVariables,
  options?: any
) =>
  useInfiniteQuery<GraphQLResult<ListHabitatsQuery>, Error, HabitatModel[]>(
    habitatQueryKeys.list(params),
    ({ pageParam }) => listHabitatsQuery({ ...params, nextToken: pageParam }),
    {
      getNextPageParam: ({ data }) => data.listHabitats?.nextToken,
      select: ({ pages, ...data }) => ({
        ...data,
        pages: pages.flatMap(({ data }) =>
          (
            data.listHabitats?.items?.filter(notNullOrUndefined) as Habitat[]
          )?.map(formatHabitatToModel)
        ),
      }),
      ...options,
    }
  );

export const useListHabitatsByHabitatIDQuery = (
  params?: HabitatByHabitatIDQueryVariables,
  options?: any
) =>
  useQuery(
    habitatQueryKeys.list(params),
    () => listHabitatsByHabitatId(params),
    options
  );

export const useGetHabitatByHabitatID = (habitatID?: string) => {
  const { data, ...results } = useListHabitatsByHabitatIDQuery(
    { habitatID: habitatID as string },
    { enabled: !!habitatID }
  );

  const habitat = useMemo(() => {
    const h = data?.find((h) => h.habitatID === habitatID);
    return h && formatHabitatToModel(h);
  }, [data, habitatID]);

  return {
    ...results,
    habitat,
  };
};
